/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2022-2023 (original work) Open Assessment Technologies SA ;
 */

import pluginFactory from 'taoTests/runner/plugin';
import { testSessionStatus } from '../../session/sessionStates.js';
import { getTestSessionStatusStore } from '../../testsStateStore.js';
import { __, humanizeTime } from '@oat-sa-private/ui-core';
import {
    isPausedByProctorExecution,
    updatePausedByProctorExecution,
    isPausedByProctorUiFlow,
    isProctoredSession
} from '../../util/proctoring';
import { showNavigationFeedback, getNavigationFeedbacksStore } from '../../feedback';
import ProctorWait from './ProctorWait.svelte';
import { showNotification } from '@oat-sa-private/ui-components';
import NetworkError from 'core/error/NetworkError';
import { getTimersStore } from '../../timers/timersStore.js';
import { getNavigationFeedbackConfig } from 'testRunnerDynamicModulesIndex';

/**
 * the proctoring plugin handles real-time Proctoring ACS actions:
 *  - pause test-runner (temporarily stop execution)
 *  - resume test-runner from paused state
 *  - add extra time for test with timers
 *  - terminate test-runner
 */
export default pluginFactory({
    name: 'proctoring',

    install() {},

    init() {
        const testRunner = this.getTestRunner();
        //socketProxy may be null; then continue to work - events may be triggered by other means (action response errorCode)
        const socketProxy = testRunner.socketProxy;

        if (!isProctoredSession(testRunner.getTestContext())) {
            return;
        }

        const testConfig = testRunner.getConfig();
        const serviceCallId = testConfig.serviceCallId;
        const areaBroker = testRunner.getAreaBroker();
        const testSessionStatusStore = getTestSessionStatusStore(serviceCallId);
        const timersStore = getTimersStore(serviceCallId);
        const navigationFeedbacksStore = getNavigationFeedbacksStore(serviceCallId);

        this.proctorWaitComponent = null;
        this.extraTimeStr = null;

        /**
         * Show notification about extra time change
         * @param {String} notificationText
         */
        this.showExtraTimeNotification = notificationText => {
            if (notificationText) {
                showNotification(
                    {
                        title: notificationText,
                        hierarchy: 'neutral',
                        closeable: true
                    },
                    'persistent'
                );
            }
        };

        /**
         * Subscribe to socket messages from proctoring and fire corresponding test-runner event:
         * @fires proctor-terminate
         * @fires proctor-pause
         * @fires proctor-resume
         * @fires proctor-extratime payload: { extraTimeMs: Number }
         *
         * If messages is received during some transition (item loading etc.),
         *  postpone emitting it until transition finishes with `proctor-socket-subscribe`/`proctor-socket-unsubscribe`.
         * So each test-runner transition (promise like enableItem or showing validation dialog) should implement one of these:
         *   a) Use `proctor-socket-subscribe`/`proctor-socket-unsubscribe` to ensure it won't be interrupted at unexpected time
         *   b) Implement cancellation if test-runner gets into the `pause` state while it executes.
         */
        this.listenToSocketProxy = () => {
            let isSubscribed = true;
            let queuedEvents = [];

            testRunner.on('proctor-socket-subscribe', () => {
                isSubscribed = true;
                if (queuedEvents.length) {
                    queuedEvents.reverse();
                    const terminateEvent = queuedEvents.find(qe => qe[0] === 'proctor-terminate');
                    if (terminateEvent) {
                        testRunner.trigger(...terminateEvent);
                    } else {
                        const lastPauseOrResumeEvent = queuedEvents.find(
                            qe => qe[0] === 'proctor-pause' || qe[0] === 'proctor-resume'
                        );
                        if (lastPauseOrResumeEvent) {
                            testRunner.trigger(...lastPauseOrResumeEvent);
                        }
                        const lastExtraTimeEvent = queuedEvents.find(qe => qe[0] === 'proctor-extratime');
                        if (lastExtraTimeEvent) {
                            testRunner.trigger(...lastExtraTimeEvent);
                        }
                    }
                    queuedEvents = [];
                }
            });
            testRunner.on('proctor-socket-unsubscribe', () => {
                isSubscribed = false;
            });

            socketProxy.on('proctoring-acs-action', msg => {
                const triggerOrQueue = (...args) => {
                    if (isSubscribed) {
                        testRunner.trigger(...args);
                    } else {
                        queuedEvents.push([...args]);
                    }
                };
                switch (msg.action) {
                    case 'pause':
                        triggerOrQueue('proctor-pause');
                        break;
                    case 'resume':
                        triggerOrQueue('proctor-resume');
                        break;
                    case 'terminate':
                        triggerOrQueue('proctor-terminate');
                        break;
                }
                if (typeof msg.extra_time === 'number') {
                    triggerOrQueue('proctor-extratime', {
                        extraTimeMs: msg.extra_time * 60 * 1000 //minutes to milliseconds; can be 0 or 0.25
                    });
                }
            });
            socketProxy.on('proctoring-acs-action-error', msg => {
                testRunner.trigger('error', new NetworkError(msg));
            });
        };

        /**
         * Mount component for the paused state, set corresponding testSessionStatus,
         * Handle component's "Resume" button click (which is the only way to resume a paused session)
         * @param {Boolean} skipProctorDialog
         */
        this.showProctorWaitScreen = skipProctorDialog => {
            testSessionStatusStore.set(testSessionStatus.proctorwait);

            this.proctorWaitComponent = new ProctorWait({
                target: areaBroker.getMainArea(),
                props: {
                    serviceCallId,
                    extraTimeStr: this.extraTimeStr,
                    hideContent: !skipProctorDialog
                }
            });
            this.proctorWaitComponent.$on('resume', () => {
                testRunner.on('resume.proctorwait', () => {
                    testRunner.off('resume.proctorwait');

                    this.cancelBackupResumePolling();
                    this.proctorWaitComponent.$destroy();
                    this.proctorWaitComponent = null;
                    testSessionStatusStore.set(testSessionStatus.loading);

                    const currentItemIdentifier = testRunner.getTestContext().itemIdentifier;
                    if (testRunner.getItemState(currentItemIdentifier, 'disabled')) {
                        testRunner.on('enableitem.proctorwait', () => {
                            testRunner.off('enableitem.proctorwait');
                            this.showExtraTimeNotification(this.extraTimeStr);
                        });
                        testRunner.enableItem(currentItemIdentifier);
                    } else {
                        testRunner.on('renderitem.proctorwait', () => {
                            testRunner.off('renderitem.proctorwait');
                            this.showExtraTimeNotification(this.extraTimeStr);
                        });
                        //= `qti.startItemSession()` - from where `computeNextAction` on `init`->`render` left off
                        testRunner.jump(testRunner.getTestContext().itemPosition);
                    }
                });
                testRunner.resume();
            });
        };

        /**
         * After test-runner was paused with api `testRunner.pause()` method,
         *  set up corresponding ui state: disable item, mount waiting screen component and show feedback dialog
         * @param {Boolean} skipProctorDialog - do not show feedback dialog (if initial paused after page refresh)
         */
        this.startProctorPauseUiFlow = skipProctorDialog => {
            this.extraTimeStr = null;

            testRunner.trigger('timersservice-stop');
            testRunner.trigger('disablenav');
            navigationFeedbacksStore.cancel();

            new Promise(resolve => {
                const currentItemIdentifier = testRunner.getTestContext().itemIdentifier;
                if (
                    testRunner.getItemState(currentItemIdentifier, 'loaded') &&
                    !testRunner.getItemState(currentItemIdentifier, 'disabled')
                ) {
                    testRunner.on('disableitem.proctorwait', () => {
                        testRunner.off('disableitem.proctorwait');
                        resolve();
                    });
                    testRunner.disableItem(currentItemIdentifier);
                } else {
                    resolve();
                }
            }).then(() => {
                this.showProctorWaitScreen(skipProctorDialog);
                this.startBackupResumePolling();

                if (!skipProctorDialog) {
                    const testContext = testRunner.getTestContext();
                    const timersExistForContext = timersStore.getTimersForContext(testContext).length > 0;

                    const feedbackTypeArgs = { isPausedByProctor: true };
                    const feedbackConfig = getNavigationFeedbackConfig(feedbackTypeArgs, {
                        timersExistForContext
                    });
                    showNavigationFeedback(feedbackTypeArgs, feedbackConfig)
                        .then(feedbackResult => {
                            if (feedbackResult && feedbackResult.cancelled) {
                                return;
                            }
                            if (this.proctorWaitComponent) {
                                this.proctorWaitComponent.$set({ hideContent: false });
                            }
                        })
                        .catch(err => {
                            testRunner.trigger('error', err);
                        });
                }
            });
        };

        /**
         * If socket is disconnected/disabled, try to receive 'resume/pause' through polling `init` action.
         * If socket is connected, or session is not paused, does nothing.
         */
        this.startBackupResumePolling = () => {
            this.resumePollingInterval = setInterval(() => {
                if (
                    (!socketProxy || !socketProxy.isConnected()) &&
                    isPausedByProctorExecution(testRunner.getTestContext())
                ) {
                    testRunner
                        .getProxy()
                        .callTestAction('init')
                        .then(results => {
                            if (
                                this.resumePollingInterval &&
                                (!socketProxy || !socketProxy.isConnected()) &&
                                isPausedByProctorExecution(testRunner.getTestContext()) &&
                                results &&
                                results.testContext &&
                                !isPausedByProctorExecution(results.testContext)
                            ) {
                                testRunner.trigger('proctor-resume');
                            }
                        })
                        .catch(err => {
                            if (
                                this.resumePollingInterval &&
                                (!socketProxy || !socketProxy.isConnected()) &&
                                isPausedByProctorExecution(testRunner.getTestContext())
                            ) {
                                testRunner.trigger('error', err);
                            }
                        });
                }
            }, 20 * 1000);
        };

        this.cancelBackupResumePolling = () => {
            clearInterval(this.resumePollingInterval);
            this.resumePollingInterval = null;
        };

        testRunner.on('proctor-pause', () => {
            if (!isPausedByProctorExecution(testRunner.getTestContext())) {
                testRunner.setTestContext(updatePausedByProctorExecution(testRunner.getTestContext(), true));
            }
            if (!isPausedByProctorUiFlow(testRunner)) {
                testRunner.on('pause.proctorwait', () => {
                    testRunner.off('pause.proctorwait');
                    const currentItemIdentifier = testRunner.getTestContext().itemIdentifier;
                    const skipProctorDialog = !testRunner.getItemState(currentItemIdentifier, 'loaded'); //if from `testRunner.init()`
                    this.startProctorPauseUiFlow(skipProctorDialog);
                });
                testRunner.pause();
            }
        });

        testRunner.on('proctor-resume', () => {
            if (!isPausedByProctorExecution(testRunner.getTestContext())) {
                return;
            }
            testRunner.setTestContext(updatePausedByProctorExecution(testRunner.getTestContext(), false));
        });

        testRunner.on('proctor-terminate', () => {
            testRunner.trigger('timersservice-stop'); //testRunner.off('timeout');
            testRunner.off('proctor-pause');
            testRunner.off('proctor-resume');
            testRunner.off('proctor-terminate');

            navigationFeedbacksStore.cancel();

            const feedbackTypeArgs = { isTerminatedByProctor: true };
            const feedbackConfig = getNavigationFeedbackConfig(feedbackTypeArgs, {});
            showNavigationFeedback(feedbackTypeArgs, feedbackConfig)
                .then(() => testRunner.finish())
                .catch(() => testRunner.finish());
        });

        testRunner.on('proctor-extratime', ({ extraTimeMs }) => {
            const timeStr = humanizeTime(Math.trunc(extraTimeMs / 1000));
            const extraTimer = timersStore.getTimerFor('extra');
            const extraTimeExisted = extraTimer && extraTimer.timerValue.timeAssigned > 0;
            let extraStr;
            if (extraTimeExisted && extraTimeMs === 0) {
                extraStr = __('Extra time has been removed');
            } else if (extraTimeExisted) {
                extraStr = __('Extra time limit adjusted to %s', timeStr);
            } else if (extraTimeMs > 0) {
                extraStr = __('%s of extra time granted', timeStr);
            }

            if (isPausedByProctorUiFlow(testRunner)) {
                //we are in paused flow, show extra on the next resume screen, and notification once that screen is closed
                this.extraTimeStr = extraStr;
                if (this.proctorWaitComponent) {
                    this.proctorWaitComponent.$set({
                        extraTimeStr: extraStr
                    });
                }
            } else {
                //proctoring event is only used to show notification;
                //actual adding of time is handled in the timers service:
                //  timer messages sent by backend will have 'extra' property filled
                this.showExtraTimeNotification(extraStr);
            }
        });

        testRunner.on('security-showed', e => {
            if (!e.autoresume && !isPausedByProctorUiFlow(testRunner)) {
                testRunner.setTestContext(updatePausedByProctorExecution(testRunner.getTestContext(), true));
                testRunner.pause();
                testRunner.trigger('timersservice-stop');

                const currentItemIdentifier = testRunner.getTestContext().itemIdentifier;
                if (
                    testRunner.getItemState(currentItemIdentifier, 'loaded') &&
                    !testRunner.getItemState(currentItemIdentifier, 'disabled')
                ) {
                    testRunner.disableItem(currentItemIdentifier);
                }
                // inform backend about pause
                const proxy = testRunner.getProxy();
                proxy.callTestAction('pause', e);
            }
        });
        testRunner.on('security-closed', e => {
            if (!e.autoresume && isPausedByProctorUiFlow(testRunner)) {
                this.showProctorWaitScreen(true);
            }
        });

        if (socketProxy) {
            this.listenToSocketProxy();
        }
    },

    render() {},

    destroy() {
        this.getTestRunner().off('.proctorwait');

        if (this.proctorWaitComponent) {
            this.proctorWaitComponent.$destroy();
        }

        if (this.resumePollingInterval) {
            clearInterval(this.resumePollingInterval);
        }
    }
});
